import { runningClientSide } from '@benzinga/utils';

const SCRIPT_ELEMENT_ID = 'google-tag-manager';
const NO_SCRIPT_ELEMENT_ID = 'no-script-google-tag-manager';

interface Props {
  measurementId: string;
  logger?: boolean;
}

type MeasurementId = string;
type Properties = Record<string, any>;

export class GoogleTagAnalytics {
  private static instance: GoogleTagAnalytics;
  public dataLayer: Properties[];
  public measurementId: MeasurementId;
  public started: boolean;
  private logger: boolean;

  constructor(config: Props) {
    if (!config) {
      this.error('Config is not provided!');
    }

    if (!config.measurementId) {
      this.error('Measurement Id is not provided!');
    }

    const { logger, measurementId } = config;
    this.measurementId = measurementId;
    this.logger = logger ?? false;
    this.started = false;

    this.dataLayer = [];
    if (runningClientSide()) {
      this.dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer = this.dataLayer;
    }

    return this.load();
  }

  public static getInstance(config: Props): GoogleTagAnalytics {
    if (!GoogleTagAnalytics.instance) {
      GoogleTagAnalytics.instance = new GoogleTagAnalytics(config);
    }

    return GoogleTagAnalytics.instance;
  }

  public static initialize(config: Props): GoogleTagAnalytics {
    const instance = GoogleTagAnalytics.getInstance(config);

    if (runningClientSide()) {
      (window as any).gtagAnalytics = instance;
    }

    return instance;
  }

  load(): GoogleTagAnalytics {
    if (runningClientSide()) {
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        this.loadScripts();
      } else {
        document.addEventListener('DOMContentLoaded', this.loadScripts.bind(this));
      }
    }

    return this;
  }

  start() {
    this.started = true;
    this.track('gtm.js', {
      'gtm.start': new Date().getTime(),
    });
  }

  track(eventName: string, eventData: Properties): GoogleTagAnalytics {
    this.pushData({
      event: eventName,
      ...eventData,
    });

    return this;
  }

  trackPage(pageUrl: string, dimensions: Properties): GoogleTagAnalytics {
    this.pushData({
      event: 'page_view',
      page_url: pageUrl,
      ...dimensions,
    });

    if (!this.started) {
      this.start();
    }

    return this;
  }

  identify(uid: string | number, properties: Properties): GoogleTagAnalytics {
    this.pushData({
      user_id: uid,
      ...properties,
    });

    return this;
  }

  error(message: string): void {
    console.error(`Google Tag Manager ERROR:: ${message}`);
  }

  private pushData(eventData: Properties): void {
    if (!eventData) {
      this.error('DataLayer data is not provided');

      return;
    }

    this.dataLayer?.push(eventData);

    if (this.logger) {
      console.log(`Google Push Data::${JSON.stringify(eventData)}`);
    }
  }

  private loadScript(): void {
    if (runningClientSide()) {
      if (!document.getElementById(SCRIPT_ELEMENT_ID)) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${this.measurementId}&l=dataLayer`;
        script.id = SCRIPT_ELEMENT_ID;
        script.onload = () => {
          if (!this.started) {
            this.start();
          }
        };

        const loadedScript = document.querySelector('script');
        loadedScript?.parentNode?.insertBefore(script, loadedScript);
      }
    }
  }

  private insertNoScript(): void {
    if (runningClientSide()) {
      if (!document.getElementById(NO_SCRIPT_ELEMENT_ID)) {
        const noScript = document.createElement('noscript');
        const iframe = document.createElement('iframe');

        iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.measurementId}`;
        iframe.id = NO_SCRIPT_ELEMENT_ID;
        iframe.setAttribute('height', '0');
        iframe.setAttribute('width', '0');
        iframe.setAttribute('style', 'display:none;visibility:hidden');
        noScript.appendChild(iframe);

        document.body.insertAdjacentElement('afterbegin', noScript);
      }
    }
  }

  private loadScripts(): void {
    this.insertNoScript();
    this.loadScript();
  }
}
